<div class="page nf-page">

  @if (loading) {
    <app-loader label="Loading notifications…"/>
  } @else {

  <!-- Page head: title + summary -->
  <div class="page-head">
    <div>
      <h1 class="page-title">Notifications</h1>
      <!-- blank until the counts land, rather than "0 notifications" -->
      <p class="head-sub">
        @if (counts) {
          @if (unreadTotal) { <b>{{ formatCount(unreadTotal) }}</b> unread · }
          {{ formatCount(inboxTotal) }} {{ inboxTotal === 1 ? 'notification' : 'notifications' }}
        }
      </p>
    </div>
  </div>

  <!-- Toolbar: read-state view · source view · search · filters -->
  <div class="toolbar">
    <div class="seg" role="tablist" aria-label="Read status">
      <button type="button" role="tab" [class.on]="view === 'unread'" [attr.aria-selected]="view === 'unread'" (click)="setView('unread')">
        Unread
        @if (counts?.view?.unread) { <span class="seg-count">{{ formatCount(counts!.view.unread) }}</span> }
      </button>
      <button type="button" role="tab" [class.on]="view === 'all'" [attr.aria-selected]="view === 'all'" (click)="setView('all')">All</button>
      <button type="button" role="tab" [class.on]="view === 'read'" [attr.aria-selected]="view === 'read'" (click)="setView('read')">Read</button>
    </div>

    <div class="seg" role="tablist" aria-label="Who it came from">
      <button type="button" role="tab" [class.on]="source === 'all'" [attr.aria-selected]="source === 'all'" (click)="setSource('all')">
        All sources <span class="nf-seg-n">{{ formatCount(counts?.source?.all ?? 0) }}</span></button>
      <button type="button" role="tab" [class.on]="source === 'team'" [attr.aria-selected]="source === 'team'" (click)="setSource('team')">
        Team <span class="nf-seg-n">{{ formatCount(counts?.source?.team ?? 0) }}</span></button>
      <button type="button" role="tab" [class.on]="source === 'system'" [attr.aria-selected]="source === 'system'" (click)="setSource('system')">
        System <span class="nf-seg-n">{{ formatCount(counts?.source?.system ?? 0) }}</span></button>
    </div>

    <span class="grow"></span>

    <label class="search">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
      <!-- listing-page.css sizes the pill from an invisible copy of the placeholder -->
      <span class="s-field">
        <input #ph type="text" placeholder="Search notifications, people, records…" aria-label="Search notifications"
               [value]="query" (input)="onQuery($any($event.target).value)"/>
        <span class="s-ghost" aria-hidden="true">{{ ph.placeholder }}</span>
      </span>
    </label>

    <button type="button" class="filters-btn" [class.on]="filtersOpen || filterCount > 0"
            [attr.aria-expanded]="filtersOpen" (click)="filtersOpen = !filtersOpen">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
      Filters
      @if (filterCount > 0) { <span class="f-count">{{ filterCount }}</span> }
    </button>

    <!-- Every unread notification, whatever the filters; asks first. Off when
         nothing is unread. -->
    <button type="button" class="filters-btn nf-mark" [disabled]="!unreadTotal || markingAll"
            (click)="markAllRead()">Mark all as read</button>
  </div>

  <!-- On-demand filter panel (hidden, not destroyed - filters keep state) -->
  <div class="filter-panel" [hidden]="!filtersOpen">
    <div class="f-field fdd nf-type-field" (click)="$event.stopPropagation()">
      <span>Type</span>
      <button type="button" class="fdd-btn" [class.set]="!!type" aria-haspopup="listbox"
              [attr.aria-expanded]="typeOpen" (click)="toggleMenu('type', $event)">
        <span class="nf-dd-val">
          @if (type) { <i class="nf-tdot" [attr.data-type]="type"></i> }
          <span class="nf-ell">{{ typeFilterLabel(type) }}</span>
        </span>
        <svg class="caret" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
      </button>
      @if (typeOpen) {
        <div class="dd-menu dd-wide" role="listbox">
          <div class="dd-opts">
            <button type="button" class="dd-opt" [class.on]="!type" (click)="setType('')">All types</button>
            <!-- counts under the other filters; a zero is muted, still pickable -->
            @for (option of types; track option.id) {
              <button type="button" class="dd-opt nf-opt" [class.on]="type === option.id" [class.nf-none]="!typeCount(option.id)"
                      (click)="setType(option.id)">
                <i class="nf-tdot" [attr.data-type]="option.id"></i><span class="nf-ell">{{ option.label }}</span>
                <span class="nf-opt-n">{{ formatCount(typeCount(option.id)) }}</span>
              </button>
            }
          </div>
        </div>
      }
    </div>

    <div class="f-field fdd" (click)="$event.stopPropagation()">
      <span>From</span>
      <button type="button" class="fdd-btn" [class.set]="!!personId" aria-haspopup="listbox"
              [attr.aria-expanded]="personOpen" (click)="toggleMenu('person', $event)">
        <span class="nf-dd-val">
          @if (person; as picked) {
            <span class="nf-av">
              @if (personPhoto(picked); as photo) { <img [src]="photo" [alt]="picked.name" (error)="onPersonPhotoError(picked.id)"/> }
              @else { {{ picked.initials }} }
            </span>
          }
          <span class="nf-ell">{{ person?.name || 'Everyone' }}</span>
        </span>
        <svg class="caret" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
      </button>
      @if (personOpen) {
        <!-- search-first picker over the full staff list -->
        <div class="dd-menu dd-wide" role="listbox">
          <input #personSearch type="text" class="dd-search" placeholder="Search people…" [value]="personQuery"
                 (input)="personQuery = $any($event.target).value"/>
          <div class="dd-opts">
            @if (!personQuery.trim()) {
              <button type="button" class="dd-opt" [class.on]="!personId" (click)="setPerson(null)">Everyone</button>
            }
            @for (member of peopleHits; track member.id) {
              <button type="button" class="dd-opt nf-opt" [class.on]="personId === member.id" [class.nf-none]="!personCount(member.id)"
                      (click)="setPerson(member.id)">
                <span class="nf-av">
                  @if (personPhoto(member); as photo) { <img [src]="photo" [alt]="member.name" (error)="onPersonPhotoError(member.id)"/> }
                  @else { {{ member.initials }} }
                </span>
                <span class="nf-ell">{{ member.name }}</span>
                <span class="nf-opt-n">{{ formatCount(personCount(member.id)) }}</span>
              </button>
            } @empty {
              <div class="dd-none">No one matches “{{ personQuery }}”</div>
            }
          </div>
        </div>
      }
    </div>

    <div class="f-field">
      <span>Received</span>
      <app-date-range-filter #received [optional]="true" align="left" variant="field" (rangeChange)="onRange($event)"/>
    </div>

    <span class="grow"></span>
    @if (filterCount > 0) {
      <button type="button" class="clear-btn" (click)="clearFilters()">Clear all</button>
    }
  </div>

  <!-- active filters wear removable chips - same affordance as the listings -->
  @if (filterCount > 0) {
    <div class="chips-row">
      @if (type) { <span class="f-chip">Type: {{ typeFilterLabel(type) }} <button type="button" aria-label="Remove type filter" (click)="setType('')">✕</button></span> }
      @if (personId) { <span class="f-chip">From: {{ person?.name }} <button type="button" aria-label="Remove person filter" (click)="setPerson(null)">✕</button></span> }
      @if (range) { <span class="f-chip">Received: {{ range.label }} <button type="button" aria-label="Remove date filter" (click)="removeRange()">✕</button></span> }
    </div>
  }

  @if (rows.length) {
    <p class="nf-count">
      @if (remaining > 0) { Showing <b>{{ formatCount(rows.length) }}</b> of {{ formatCount(total) }} }
      @else { {{ formatCount(total) }} {{ total === 1 ? 'notification' : 'notifications' }} }
    </p>

    <section class="nf-list" [attr.aria-busy]="refreshing" aria-label="Notifications">
      @for (group of groups; track group.key) {
        <h2 class="nf-day">{{ group.label }} <span>{{ group.items.length }}</span></h2>
        @for (row of group.items; track row.id) {
          <article class="nf-row" [class.unread]="isUnread(row)" [attr.data-type]="row.type" (click)="onRowClick($event, row)">
            <span class="avatar-wrap">
              <span class="avatar" [attr.data-type]="row.type">
                @if (rowPhoto(row); as photo) { <img [src]="photo" [alt]="row.sender" (error)="onRowPhotoError(row)"/> }
                @else { {{ row.initials }} }
              </span>
              <span class="type-badge" [attr.data-type]="row.type" [attr.aria-label]="typeLabel(row.type)">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                  @switch (row.type) {
                    @case ('order') { <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/> }
                    @case ('quote') { <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/> }
                    @case ('sample') { <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"/> }
                    @case ('mention') { <path d="M12 2a10 10 0 1 0 4 19.2"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.9 7.9"/><circle cx="12" cy="12" r="4"/> }
                    @default { <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/> }
                  }
                </svg>
              </span>
            </span>

            <div class="nf-main">
              <!-- the title links to the record; title, message and detail values mark the search hits -->
              @if (row.target) {
                <a class="nf-title" [routerLink]="row.target.commands" [queryParams]="row.target.queryParams"><ng-container *ngTemplateOutlet="segsTpl; context: {$implicit: row.title}"/></a>
              } @else {
                <span class="nf-title"><ng-container *ngTemplateOutlet="segsTpl; context: {$implicit: row.title}"/></span>
              }
              <!-- the full message - never clipped (GC-354) -->
              <div class="nf-text" [innerHTML]="row.html"></div>
              @if (row.chip; as chip) {
                <div class="nf-meta">
                  <span class="ref-chip" [attr.data-type]="chip.kind">{{ chip.label }} · {{ chip.ref }}</span>
                </div>
              }
            </div>

            <div class="nf-when">
              <span class="nf-ago">
                @if (isUnread(row)) { <i class="nf-dot" aria-label="Unread"></i> }
                {{ timeAgo(row.timestamp) }}
              </span>
              <span>{{ clock(row.timestamp) }}</span>
            </div>

            <!-- every action is always visible: no hover-only controls (touch rule) -->
            <div class="nf-acts">
              @if (canLike(row)) {
                <button type="button" class="nf-act tip" [class.liked]="isLiked(row)"
                        [attr.data-tip]="isLiked(row) ? 'Unlike' : 'Like - also marks it read'"
                        [attr.aria-label]="isLiked(row) ? 'Unlike' : 'Like'" [attr.aria-pressed]="isLiked(row)" (click)="toggleLike(row)">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                </button>
              } @else {
                <span class="nf-act nf-act-gap" aria-hidden="true"></span>
              }
              <button type="button" class="nf-act tip" [disabled]="readSaving(row)"
                      [attr.data-tip]="isUnread(row) ? 'Mark as read' : 'Mark as unread'"
                      [attr.aria-label]="isUnread(row) ? 'Mark as read' : 'Mark as unread'" (click)="toggleRead(row)">
                @if (isUnread(row)) {
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                       stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                } @else {
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 6L2 7"/></svg>
                }
              </button>
              @if (row.target) {
                <a class="nf-act tip" [routerLink]="row.target.commands" [queryParams]="row.target.queryParams" data-tip="Open"
                   [attr.aria-label]="'Open ' + (row.chip?.ref || row.titleText)">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round"><path d="M7 17L17 7M8 7h9v9"/></svg>
                </a>
              } @else {
                <span class="nf-act nf-act-gap" aria-hidden="true"></span>
              }
            </div>
          </article>
        }
      }
    </section>

    <!-- lazy loading: the marker below the list fetches the next page as it
         nears the viewport; Show more is the keyboard / fallback path -->
    <div #sentinel class="nf-sentinel" aria-hidden="true"></div>
    @if (loadingMore) {
      <div class="nf-loading" role="status">
        <svg width="16" height="16" viewBox="0 0 32 32" aria-hidden="true">
          <circle cx="16" cy="16" r="13" fill="none" stroke="var(--border)" stroke-width="3"/>
          <circle cx="16" cy="16" r="13" fill="none" stroke="var(--text)" stroke-width="3" stroke-linecap="round" stroke-dasharray="22 62">
            <animateTransform attributeName="transform" type="rotate" from="0 16 16" to="360 16 16" dur="0.8s" repeatCount="indefinite"/>
          </circle>
        </svg>
        Loading more…
      </div>
    } @else if (remaining > 0) {
      <button type="button" class="nf-more" (click)="loadMore()">Show more ({{ formatCount(remaining) }} older)</button>
    } @else {
      <p class="nf-end">That's everything{{ narrowed ? ' for these filters' : '' }}.</p>
    }

  } @else {
    <div class="nf-empty">
      <span class="nf-empty-ic">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"
             stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
      </span>
      <p class="nf-empty-t">{{ emptyTitle }}</p>
      <p class="nf-empty-s">{{ emptyText }}</p>
      @if (narrowed) {
        <button type="button" class="btn-secondary" (click)="clearAll()">Clear filters</button>
      }
    </div>
  }

  }
</div>

<!-- one run of title text with its search hits marked; plain runs in spans so
     no template whitespace leaks into the text -->
<ng-template #segsTpl let-segs>@for (s of segs; track $index) {@if (s.hit) {<mark>{{ s.text }}</mark>} @else {<span>{{ s.text }}</span>}}</ng-template>
